/* -------------------------------------------------------------------------- */
/* 1. Les registres                                                           */
/* -------------------------------------------------------------------------- */
/** Les deux fichiers de la partie « savoir », et les deux dossiers à côté. */
export const FICHIER_ERREURS = 'erreurs.json';
export const FICHIER_CAPSULES = 'capsules.json';
/** Une fiche par fichier, nommé par son code : `fiches/MAT-3-EXP-F2.json`. */
export const DOSSIER_FICHES = 'fiches';
/**
 * Les MP4, affiches et sous-titres d'un chapitre. Ce dossier N'EST PAS SUIVI
 * PAR GIT (`.gitignore`, `.easignore`) : les médias vivent au stockage objet
 * (AD-MV-18), et l'import les y envoie.
 */
export const DOSSIER_MEDIAS = 'medias';
/** La nature d'un média éditorial. */
export const NATURES_MEDIA = ['video', 'affiche', 'sous_titres'];
/** Les types de fichier admis, par nature. Tout autre type est refusé à l'import. */
export const MIME_DES_MEDIAS = {
    video: { extension: '.mp4', mime: 'video/mp4' },
    affiche: { extension: '.jpg', mime: 'image/jpeg' },
    sous_titres: { extension: '.vtt', mime: 'text/vtt' },
};
/** Plafond d'une capsule (`DECISIONS-ARCHITECTE.md`, point 20) : trois minutes. */
export const DUREE_MAX_CAPSULE_SEC = 180;
/** Les familles d'éléments que l'import de la partie « savoir » écrit. */
export const FAMILLES_IMPORT_SAVOIR = ['erreur', 'fiche', 'media', 'capsule', 'remede'];
/**
 * Le libellé d'un contenu que personne n'a encore relu (MV-CNT-05, AD-MV-8).
 * Une seule écriture, lue par la console et par la recette.
 */
export const LIBELLE_NON_RELU = 'Produit par agent — non relu';
/**
 * Les transitions de statut praticables dans la console (AD-MV-19). Le contenu
 * importé naît `publie` ; on le SORT du service pour le reprendre (`en_revue`),
 * on le valide, on le republie. `retire` est définitif : un contenu retiré ne
 * revient pas, une version neuve le remplace.
 */
export const TRANSITIONS_STATUT_SAVOIR = {
    brouillon_IA: ['en_revue'],
    en_revue: ['valide', 'retire'],
    valide: ['publie', 'en_revue'],
    publie: ['en_revue', 'retire'],
    rejete: [],
    retire: [],
};
/** Une transition est-elle admise ? */
export function transitionSavoirAdmise(de, vers) {
    return TRANSITIONS_STATUT_SAVOIR[de].includes(vers);
}
/** « 1 min 14 », « 45 s », « 2 min ». */
export function dureeEnClair(secondes) {
    const s = Math.max(0, Math.round(secondes));
    const minutes = Math.floor(s / 60);
    const reste = s % 60;
    if (minutes === 0)
        return `${reste} s`;
    return reste === 0 ? `${minutes} min` : `${minutes} min ${String(reste).padStart(2, '0')}`;
}
const HORODATAGE_VTT = /^(?:(\d{2,}):)?(\d{2}):(\d{2})\.(\d{3})$/;
function instantVtt(brut) {
    const m = HORODATAGE_VTT.exec(brut.trim());
    if (!m)
        return null;
    const [, h, min, s, ms] = m;
    return ((Number(h ?? 0) * 60 + Number(min)) * 60 + Number(s)) * 1000 + Number(ms);
}
/**
 * Lit un fichier WebVTT et rend ses répliques, dans l'ordre. `null` si le
 * fichier n'en est pas un, ou si une réplique est mal formée — l'import refuse
 * alors la capsule plutôt que de servir des sous-titres troués.
 *
 * Les répliques sont lues UNE fois, à l'import, et rangées avec la capsule :
 * l'application écrit elle-même la réplique courante sous l'image (MV-SAV-06 —
 * rendu identique sur les trois plateformes), sans avoir à télécharger ni à
 * lire le fichier.
 */
export function lireSousTitresVtt(source) {
    /* Une marque d'ordre d'octets (U+FEFF) en tête de fichier n'est pas du texte. */
    const lignes = (source.charCodeAt(0) === 0xfeff ? source.slice(1) : source).split(/\r?\n/);
    if (!(lignes[0] ?? '').startsWith('WEBVTT'))
        return null;
    const reperes = [];
    let i = 1;
    while (i < lignes.length) {
        const ligne = (lignes[i] ?? '').trim();
        if (!ligne.includes('-->')) {
            i += 1;
            continue;
        }
        const [debut, suite] = ligne.split('-->');
        const debutMs = instantVtt(debut ?? '');
        const finMs = instantVtt((suite ?? '').trim().split(/\s+/)[0] ?? '');
        if (debutMs === null || finMs === null || finMs <= debutMs)
            return null;
        const texte = [];
        i += 1;
        while (i < lignes.length && (lignes[i] ?? '').trim() !== '') {
            texte.push(lignes[i].trim());
            i += 1;
        }
        if (texte.length === 0)
            return null;
        reperes.push({ debutMs, finMs, texte: texte.join('\n') });
    }
    for (let k = 1; k < reperes.length; k += 1) {
        if (reperes[k].debutMs < reperes[k - 1].debutMs) {
            return null;
        }
    }
    return reperes.length === 0 ? null : reperes;
}
/** La réplique à afficher à cet instant de la lecture ; `null` entre deux répliques. */
export function repereCourant(reperes, positionMs) {
    return reperes.find((r) => positionMs >= r.debutMs && positionMs < r.finMs) ?? null;
}
/** Une entrée de `capsules.json` est-elle « à venir » ? */
export const capsuleEstAVenir = (c) => c.aVenir === true;
/**
 * L'état d'une capsule face à l'élève (T40) : `servie`, elle se regarde ;
 * `a_venir`, elle est annoncée — « Cette capsule arrive. » —, sans lecteur.
 */
export const ETATS_CAPSULE = ['servie', 'a_venir'];
/** La capsule ouverte est-elle à venir ? */
export const capsuleOuverteAVenir = (c) => c.video === null;
/** Les trois sortes de contenu rédigé que la console relit. */
export const SORTES_SAVOIR = ['fiches', 'capsules', 'erreurs'];
//# sourceMappingURL=manuel-savoir.js.map